import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, } from '@angular/router';
import { Actions, createEffect, ofType, } from '@ngrx/effects';
import { Store, } from '@ngrx/store';
import { isAuthorizedInKeycloak } from 'app/keycloak/reducers/keycloak.reducer';
import { filter, map, switchMap, withLatestFrom, } from 'rxjs/operators';
import { SearchResult } from 'search/models/search-results';
import { Edition, FormatGroup, VendorType } from '../models/entity';
import { beginHold, beginRapidoTabRequest, beginTabHold, tabHoldNlb, toggleOpenedUserPanel, } from 'user/actions/user-profile.actions';
import { tryToGetIt, tryToGetItRollup, tryToGetItTab, tryToGetItTabRapido, tryToPlaceHold, tryToPlaceHoldOnRollup } from '../actions/entity.actions';
import { PrepareExternalGetItAction, PrepareExternalHoldRequestAction, } from '../actions/vendors.actions';
import { RollupCard } from '../models/rollup-card';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { CustomerIntegrationType } from 'app/customer-integration/customer-integration';
import { VendorIssuesService } from '../services/vendor-issues.service';
import { RapidoOffersService } from 'app/services/rapido-offers.service';
import { selectEntity } from '../reducers/full-entity.reducer';
import { getUser } from 'user/reducers/user.reducer';


@Injectable()
export class GetItEffects {
  public tryToGetIt$ = createEffect(() => this.actions$.pipe(
    ofType(tryToGetIt),
    withLatestFrom(this.store.select(isAuthorizedInKeycloak)),
    filter(([{item, vendor, reserveId}, isAuthorized]) => {
      if (!isAuthorized) {
        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams: {getResourceId: item.id, getResourceVendor: vendor, getReserveId: reserveId, getRecordId: (item as Edition).recordId},
          queryParamsHandling: 'merge',
        }).toString();
        this.location.go(tree);
        this.store.dispatch(toggleOpenedUserPanel({isOpened: true}));
      }

      return isAuthorized;
    }),
    map(([{item, vendor, reserveId}]) => {
      const hasEcontentIssues = this.vendorIssuesService.hasVendorIssuesForEdition(item as Edition);
      return vendor && reserveId ? new PrepareExternalGetItAction({
        entityType: (item as SearchResult | RollupCard).entityType,
        formatGroupId: (item as SearchResult | RollupCard).formatGroupId,
        instanceId: (item as SearchResult | RollupCard).instanceId,
        vendor,
        itemId: item.id,
        reserveId,
        hasEcontentIssues: hasEcontentIssues,
      }) : beginHold({payload: item});
    }),
  ));

  public tryToGetItTab$ = createEffect(() => this.actions$.pipe(
    ofType(tryToGetItTab),
    withLatestFrom(this.store.select(isAuthorizedInKeycloak)),
    filter(([{cardInfo}, isAuthorized]) => {
      if (!isAuthorized) {
        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams: {
            getTabFgId: cardInfo.id,
            getTabName: cardInfo.tabName
          },
          queryParamsHandling: 'merge',
        }).toString();
        this.location.go(tree);
        this.store.dispatch(toggleOpenedUserPanel({isOpened: true}));
      }

      return isAuthorized;
    }),
    map(([{cardInfo}]) => {
      if (this.configLoader.customerIntegrationType === CustomerIntegrationType.NLB) {
        return tabHoldNlb({ payload: cardInfo });
      } else {
        return beginTabHold({ payload: cardInfo });
      }
    }),
  ));

  public tryToGetItRollupCard$ = createEffect(() => this.actions$.pipe(
    ofType(tryToGetItRollup),
    withLatestFrom(this.store.select(isAuthorizedInKeycloak)),
    filter(([{item}, isAuthorized]) => {
      if (!isAuthorized) {
        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams: {getResourceId: item.id, getResourceVendor: item.vendor, getReserveId: item.reserveId},
          queryParamsHandling: 'merge',
        }).toString();
        this.location.go(tree);
        this.store.dispatch(toggleOpenedUserPanel({isOpened: true}));
      }

      return isAuthorized;
    }),
    map(([{item}]) => {
      return item.reserveId && item.vendor ? new PrepareExternalGetItAction({
        entityType: item.entityType,
        formatGroupId: item.formatGroupId,
        instanceId: item.instanceId,
        vendor: item.vendor,
        itemId: item.id,
        reserveId: item.reserveId,
        readyHold: item.readyHold,
        hasEcontentIssues: item.hasEcontentIssues,
      }) : beginHold({ payload: item });
    }),
  ));

  public tryToPlaceHold$ = createEffect(() => this.actions$.pipe(
    ofType(tryToPlaceHold),
    withLatestFrom(this.store.select(isAuthorizedInKeycloak)),
    filter(([{item, vendor, reserveId}, isAuthorized]) => {
      if (!isAuthorized) {
        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams: {holdResourceId: item?.id, holdResourceVendor: vendor, holdReserveId: reserveId},
          queryParamsHandling: 'merge',
        }).toString();
        this.location.go(tree);
        this.store.dispatch(toggleOpenedUserPanel({isOpened: true}));
      }

      return isAuthorized;
    }),
    map(([{item, vendor, reserveId}]) => {
      return new PrepareExternalHoldRequestAction({
        vendor,
        itemId: item?.id,
        reserveId: reserveId || item?.vendors[vendor as VendorType].reserveId[0],
      });
    }),
  ));

  public tryToPlaceHoldOnRollup$ = createEffect(() => this.actions$.pipe(
    ofType(tryToPlaceHoldOnRollup),
    withLatestFrom(this.store.select(isAuthorizedInKeycloak)),
    filter(([{item}, isAuthorized]) => {
      if (!isAuthorized) {
        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams: {holdResourceId: item.id, holdResourceVendor: item.vendor, holdReserveId: item.reserveId},
          queryParamsHandling: 'merge',
        }).toString();
        this.location.go(tree);
        this.store.dispatch(toggleOpenedUserPanel({isOpened: true}));
      }

      return isAuthorized;
    }),
    map(([{item}]) => {
      return new PrepareExternalHoldRequestAction({
        vendor: item.vendor,
        itemId: item.id,
        reserveId: item.reserveId,
      });
    }),
  ));

  public tryToGetItTabRapido$ = createEffect(() => this.actions$.pipe(
    ofType(tryToGetItTabRapido),
    withLatestFrom(
      this.store.select(isAuthorizedInKeycloak),
      this.store.select(selectEntity),
      this.store.select(getUser),
    ),
    filter(([{cardInfo}, isAuthorized, entity, user]) => {
      if (!isAuthorized) {
        const tree = this.router.createUrlTree([], {
          relativeTo: this.route,
          queryParams: {
            getTabFgIdRapido: cardInfo.id,
            getTabTitleRapido: cardInfo.title,
            getTabRecordIdRapido: cardInfo.recordId,
            getTabNameRapido: cardInfo.tabName,
            getTabAuthorRapido: cardInfo.author,
            getTabPublicationYearRapido: cardInfo.publicationYear,
            getTabIsbnRapido: cardInfo.isbn,
            // getTabPublisherRapido: cardInfo.publisher,
            // getTabSpecificEditionRapido: cardInfo.specificEdition,
          },
          queryParamsHandling: 'merge',
        }).toString();
        this.location.go(tree);
        this.store.dispatch(toggleOpenedUserPanel({isOpened: true}));
      }

      return isAuthorized;
    }),
    switchMap(([{cardInfo}, isAuthorized, entity, user]) => {
      const fg = (entity as FormatGroup);
      return this.offersService.getOffers(cardInfo.recordId).pipe(
        map((offers) => {
          const rapidoOffer = offers?.[0];
            return beginRapidoTabRequest({
              payload: {
                id: cardInfo.id,
                title: cardInfo.title,
                recordId: cardInfo.recordId,
                user,
                tabName: cardInfo.tabName,
                author: cardInfo.author,
                publicationYear: cardInfo.publicationYear,
                isbn: cardInfo.isbn,
                fg,
                rapidoOffer,
              },
            });
        })
      );
    }),
  ));

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly router: Router,
    private readonly configLoader: ConfigurationLoader,
    private readonly vendorIssuesService: VendorIssuesService,
    private readonly offersService: RapidoOffersService,
  ) {
  }
}
